<script lang="ts">
  // Lyktan app shell. Login + MFA-flödet (S-04-02) gatar admin-vyerna.
  // Inloggad admin kan växla mellan ritvy (S-04-04 / S-04-05),
  // pilotanvändar-administration (S-04-08) och rapport-vyn (S-08-04).
  // Sessionen lagras BARA som HttpOnly-cookie på backend; klienten
  // sparar bara user-id och roll i $state-runes som försvinner vid
  // reload.

  import LoginView from './lib/auth/LoginView.svelte';
  import { logout } from './lib/auth/authClient';
  import ReportsView from './lib/reports/ReportsView.svelte';
  import CoverageSummaryView from './lib/hearth-coverage/CoverageSummaryView.svelte';
  import MfaSetupView from './lib/mfa/MfaSetupView.svelte';
  import UsersView from './lib/users/UsersView.svelte';
  import TenantsView from './lib/tenants/TenantsView.svelte';
  import SettingsView from './lib/settings/SettingsView.svelte';
  import RitaView from './lib/rita/RitaView.svelte';
  import { listSettings, parseMapDefault, type MapDefault } from './lib/settings/appSettingsClient';

  const appName = 'Lyktan';
  const subtitle = 'Härden admin';

  type Session = { userId: string; role: string };
  // Tabb-IA efter S-04-22b + S-04-17 + S-04-33:
  //  - Karta+Rundor+POI konsoliderade till 'rita' (S-04-17 Rita-tabb).
  //  - Administratörer+Pilotanvändare → 'users' (S-04-22b).
  //  - 'tenants' är superadmin-only (S-04-33). Synligheten styrs via
  //    DOM-exklusion (inte CSS-gömning) — `currentUserIsSuperadmin`
  //    avgör om tab-knappen och render-blocket ens monteras.
  //  - 'settings' är intern slug (öppnas via header-ikon, inte tabbknapp);
  //    MFA-banner-flödet (`goToMfaSetup`) sätter activeTab='settings'
  //    direkt.
  type Tab = 'rita' | 'users' | 'tenants' | 'reports' | 'coverage' | 'settings';

  let session: Session | null = $state(null);
  let isLoggingOut = $state(false);
  let activeTab: Tab = $state('rita');

  // MFA-banner / setup-state. Backend exponerar GET /api/me som
  // rapporterar mfa-status (S-04-30). Vid login fetchar vi den och
  // sätter mfaActivatedAtServer — banner visas BARA om backend
  // bekräftat att MFA INTE är aktivt. Tidigare implementation visade
  // banner proaktivt efter varje login, vilket var falskt-larm-prone
  // (Jonas-feedback: 'jag loggar hela tiden in med koden i
  // authenticator google').
  let mfaSetupOpen = $state(false);
  let mfaBannerDismissed = $state(false);
  let mfaActivatedThisSession = $state(false);
  // null = inte fetchad än (banner visas inte preventivt). false =
  // backend bekräftade ingen MFA. true = backend bekräftade MFA aktivt.
  let mfaActivatedAtServer: boolean | null = $state(null);

  // S-04-33 (Compuna-superadmin Tenants-vy). Default false så Tenants-
  // tab och vy aldrig DOM-monteras innan backend bekräftat flaggan.
  // Vi vägrar inte heller "preventivt visa" — DOM-exklusion är
  // strängare än CSS-gömning (CLAUDE.md hard-rule "default privat").
  // Server-side authorization (requireSuperadmin på /api/admin/tenants)
  // är fortfarande auktoritativ källa — flaggan här är bara UI-hint
  // för att inte visa knappar som ändå skulle returnera 403.
  let currentUserIsSuperadmin = $state(false);

  /**
   * Fetcha MFA-status + superadmin-flagga från backend. Default-
   * conservative: vid fel sätter vi inte mfaActivatedAtServer (= null)
   * så banner förblir gömd, och currentUserIsSuperadmin förblir false
   * så Tenants-tab inte renderas. Annars riskerar vi att visa banner
   * eller superadmin-tab för konton där /api/me failade tillfälligt.
   */
  async function loadMfaStatus(): Promise<void> {
    try {
      const response = await fetch('/api/me', {
        method: 'GET',
        credentials: 'include',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return;
      const body = (await response.json()) as {
        mfaActive?: boolean | null;
        isSuperadmin?: boolean;
      };
      if (typeof body.mfaActive === 'boolean') {
        mfaActivatedAtServer = body.mfaActive;
      }
      // Endast `=== true` accepteras. Saknat fält eller annan typ
      // tolkas konservativt som false → Tenants-tab döljs.
      currentUserIsSuperadmin = body.isSuperadmin === true;
    } catch {
      // Network-fel — banner förblir gömd (mfaActivatedAtServer = null)
      // och Tenants-tab förblir gömd (currentUserIsSuperadmin = false).
    }
  }

  // Admin-konfigurerade karta-defaults (S-04-22). Hämtas en gång per
  // session efter login. Tom = fall tillbaka på kompilerings-default i
  // MapView. Vi sätter inte loading-state för UX:en — användaren ser
  // hardcoded default kort om backend ännu inte har en rad, vilket är
  // acceptabelt för pilot-skala.
  let mapDefault: MapDefault | null = $state(null);

  async function loadMapDefault(): Promise<void> {
    const result = await listSettings();
    if (result.kind !== 'ok') return;
    const item = result.items.find((it) => it.key === 'map.default');
    if (!item) return;
    const parsed = parseMapDefault(item.value);
    if (parsed) mapDefault = parsed;
  }

  function handleAuthenticated(info: Session): void {
    session = info;
    // Reset MFA-banner-state vid ny inloggning. mfaActivatedAtServer
    // sätts av loadMfaStatus så banner visas BARA om backend säger
    // MFA inaktiv (S-04-30).
    mfaActivatedThisSession = false;
    mfaBannerDismissed = false;
    mfaSetupOpen = false;
    mfaActivatedAtServer = null;
    // S-04-33: nollställ superadmin-flaggan vid ny inloggning så att
    // Tenants-tab inte läcker mellan sessioner om en plain admin
    // loggar in efter en superadmin på samma device.
    currentUserIsSuperadmin = false;
    // Hämta karta-defaults + MFA-status + superadmin-flagga i
    // bakgrunden. Fel ignoreras (fallback i MapView; banner förblir
    // gömd; Tenants-tab förblir gömd).
    void loadMapDefault();
    void loadMfaStatus();
  }

  function selectTab(tab: Tab): void {
    activeTab = tab;
  }

  function openMfaSetup(): void {
    mfaSetupOpen = true;
  }

  function goToMfaSetup(): void {
    // Används av UsersView som upptäcker att admin
    // försöker utföra en MFA-gated åtgärd utan aktiv tvåfaktor. Vi tar
    // användaren till Konto-tabben och öppnar MFA-setup direkt så att
    // hen inte behöver leta i UI:t. Tidigare användes en `<a href>` till
    // `/konto/mfa`, men SPA-shellen har ingen router — länken navigerade
    // bort från Lyktan helt (S-04-16).
    activeTab = 'settings';
    mfaSetupOpen = true;
  }

  function closeMfaSetup(): void {
    mfaSetupOpen = false;
  }

  function handleMfaActivated(): void {
    mfaActivatedThisSession = true;
    mfaSetupOpen = false;
  }

  function dismissMfaBanner(): void {
    mfaBannerDismissed = true;
  }

  async function handleLogout(): Promise<void> {
    if (isLoggingOut) return;
    isLoggingOut = true;
    try {
      await logout();
    } finally {
      // Oavsett om servern svarade OK rensar vi klient-state — cookien
      // har Max-Age=0-satt i lyckat fall, och i misslyckat fall vill vi
      // ändå inte fortsätta agera som inloggad.
      session = null;
      activeTab = 'rita';
      // S-04-33: rensa superadmin-flaggan vid logout så Tenants-tab
      // inte läcker till efterföljande inloggning innan loadMfaStatus
      // hinner uppdatera värdet.
      currentUserIsSuperadmin = false;
      isLoggingOut = false;
    }
  }

  function handleUnauthorized(): void {
    // Backend har avvisat anropet — sessionen är ogiltig. Tvinga ut
    // till LoginView snarare än att låta användaren se en delvis vy.
    session = null;
    activeTab = 'rita';
    // S-04-33: samma renslogik som handleLogout — sessionsstate ska
    // alltid ner till "anonym" innan login-vyn visas igen.
    currentUserIsSuperadmin = false;
  }
</script>

<main>
  <header>
    <div class="title-row">
      <div>
        <h1>{appName} <span class="separator">—</span> {subtitle}</h1>
        <!--
          Vid !session äger LoginView sidans lead-text. Shellen lägger
          ALDRIG en egen lead ovanför så att användaren inte ser två
          stackade introtexter (S-04-16 P0-fix). Inloggad-status är
          implicit i att admin-vyer renderas (UX-fix 2026-05-05).
        -->
      </div>
      {#if session}
        <div class="header-actions">
          <button
            type="button"
            class="settings-icon"
            class:active={activeTab === 'settings'}
            aria-label="Inställningar"
            aria-pressed={activeTab === 'settings'}
            onclick={() => selectTab('settings')}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="3" />
              <path
                d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h.01a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v.01a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"
              />
            </svg>
          </button>
          <button type="button" class="logout" onclick={handleLogout} disabled={isLoggingOut}>
            {isLoggingOut ? 'Loggar ut…' : 'Logga ut'}
          </button>
        </div>
      {/if}
    </div>
  </header>

  {#if !session}
    <LoginView onAuthenticated={handleAuthenticated} />
  {:else}
    {#if mfaActivatedAtServer === false && !mfaActivatedThisSession && !mfaBannerDismissed && !mfaSetupOpen}
      <div class="mfa-banner" role="status" aria-live="polite">
        <div class="mfa-banner-text">
          <strong>Säkerhetscheck: tvåfaktor (TOTP).</strong>
          <span>
            Pilot-administration och inaktivering av konton kräver TOTP. Aktivera nu om du saknar
            det — eller dölj om du redan har TOTP uppsatt på detta konto.
          </span>
        </div>
        <div class="mfa-banner-actions">
          <button type="button" class="mfa-banner-primary" onclick={openMfaSetup}>
            Aktivera nu
          </button>
          <button
            type="button"
            class="mfa-banner-dismiss"
            onclick={dismissMfaBanner}
            aria-label="Dölj banner"
          >
            Dölj
          </button>
        </div>
      </div>
    {/if}

    {#if mfaSetupOpen}
      <MfaSetupView
        onActivated={handleMfaActivated}
        onCancel={closeMfaSetup}
        onUnauthorized={handleUnauthorized}
      />
    {:else}
      <nav class="tabs" aria-label="Adminvyer">
        <button
          type="button"
          class="tab"
          class:active={activeTab === 'rita'}
          aria-current={activeTab === 'rita' ? 'page' : undefined}
          onclick={() => selectTab('rita')}
        >
          Rita
        </button>
        <button
          type="button"
          class="tab"
          class:active={activeTab === 'users'}
          aria-current={activeTab === 'users' ? 'page' : undefined}
          onclick={() => selectTab('users')}
        >
          Användare
        </button>
        {#if currentUserIsSuperadmin}
          <!--
            Tenants-tab är Compuna-superadmin-only (S-04-33). Vi
            renderar tab-knappen via {#if}, INTE via CSS-display:none —
            så att klient-side bypass via DevTools eller tab-tangent
            inte kan navigera till vyn för plain admins. Server-side
            authorization (requireSuperadmin på /api/admin/tenants)
            är fortfarande sista försvarslinjen.
          -->
          <button
            type="button"
            class="tab"
            class:active={activeTab === 'tenants'}
            aria-current={activeTab === 'tenants' ? 'page' : undefined}
            onclick={() => selectTab('tenants')}
          >
            Tenants
          </button>
        {/if}
        <button
          type="button"
          class="tab"
          class:active={activeTab === 'reports'}
          aria-current={activeTab === 'reports' ? 'page' : undefined}
          onclick={() => selectTab('reports')}
        >
          Rapporter
        </button>
        <button
          type="button"
          class="tab"
          class:active={activeTab === 'coverage'}
          aria-current={activeTab === 'coverage' ? 'page' : undefined}
          onclick={() => selectTab('coverage')}
        >
          Täckning per runda
        </button>
      </nav>

      {#if activeTab === 'rita'}
        <RitaView
          onUnauthorized={handleUnauthorized}
          initialCenter={mapDefault ? { lng: mapDefault.lng, lat: mapDefault.lat } : undefined}
          initialZoom={mapDefault?.zoom}
        />
      {:else if activeTab === 'users'}
        <UsersView
          currentUserId={session.userId}
          onUnauthorized={handleUnauthorized}
          onMfaGateOpen={goToMfaSetup}
        />
      {:else if activeTab === 'tenants' && currentUserIsSuperadmin}
        <!--
          DOM-exklusion: render-blocket är dubbelvillkorat på
          activeTab + currentUserIsSuperadmin. Om en plain admin
          någonsin lyckas sätta activeTab='tenants' (via state-
          injection eller race) faller vi tillbaka till else-branchen
          (Settings-vyn) snarare än att rendera TenantsView över
          huvud taget.
        -->
        <TenantsView onUnauthorized={handleUnauthorized} />
      {:else if activeTab === 'reports'}
        <section class="reports-section">
          <ReportsView onUnauthorized={handleUnauthorized} />
        </section>
      {:else if activeTab === 'coverage'}
        <section class="coverage-section">
          <CoverageSummaryView onUnauthorized={handleUnauthorized} />
        </section>
      {:else}
        <SettingsView
          onOpenMfaSetup={openMfaSetup}
          {mfaActivatedThisSession}
          {mfaActivatedAtServer}
          onUnauthorized={handleUnauthorized}
        />
      {/if}
    {/if}
  {/if}

  <footer class="site-footer">
    <p>
      <a href="/integritetspolicy">Integritetspolicy</a>
      <span aria-hidden="true"> · </span>
      <span>Compuna AB</span>
    </p>
  </footer>
</main>

<style>
  :global(:root) {
    color-scheme: dark;
  }
  main {
    max-width: 1280px;
    margin: 0 auto;
    padding: 2rem 1.5rem;
    font-family:
      system-ui,
      -apple-system,
      'Segoe UI',
      Roboto,
      sans-serif;
  }
  header {
    margin-bottom: 1rem;
  }
  .title-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1.5rem;
  }
  header h1 {
    font-size: 2rem;
    margin: 0 0 0.5rem;
    letter-spacing: 0.01em;
  }
  .separator {
    opacity: 0.6;
  }
  .lead {
    margin: 0 0 2rem;
    opacity: 0.85;
  }
  .header-actions {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  .settings-icon {
    background: transparent;
    border: 1px solid rgba(232, 232, 234, 0.25);
    color: #e8e8ea;
    width: 38px;
    height: 38px;
    padding: 0;
    border-radius: 6px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-family: inherit;
  }
  .settings-icon svg {
    width: 18px;
    height: 18px;
  }
  .settings-icon.active {
    background: var(--accent-soft);
    border-color: var(--accent);
    color: var(--glow);
  }
  .settings-icon:hover:not(.active) {
    color: var(--glow);
    border-color: var(--text-dim);
  }
  .logout {
    background: transparent;
    border: 1px solid rgba(232, 232, 234, 0.25);
    color: #e8e8ea;
    padding: 0.5rem 1rem;
    border-radius: 6px;
    font-size: 0.9rem;
    cursor: pointer;
    font-family: inherit;
  }
  .logout:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
  .tabs {
    display: flex;
    gap: 0.4rem;
    margin: 0 0 1.25rem;
    border-bottom: 1px solid rgba(232, 232, 234, 0.12);
    padding-bottom: 0.5rem;
  }
  .tab {
    background: transparent;
    border: 1px solid var(--rule);
    color: var(--text-dim);
    padding: 0.5rem 1rem;
    border-radius: 6px;
    font-size: 0.9rem;
    cursor: pointer;
    font-family: inherit;
  }
  .tab.active {
    background: var(--accent-soft);
    border-color: var(--accent);
    color: var(--glow);
  }
  .reports-section {
    display: grid;
    gap: 1rem;
  }
  .mfa-banner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    flex-wrap: wrap;
    background: var(--bg-elev);
    border: 1px solid var(--accent);
    border-left: 3px solid var(--accent);
    border-radius: 4px;
    padding: 0.85rem 1rem;
    margin-bottom: 1rem;
    color: var(--text);
    box-shadow: 0 0 24px rgba(212, 160, 86, 0.08);
  }
  .mfa-banner-text {
    display: grid;
    gap: 0.15rem;
    font-size: 0.9rem;
    flex: 1 1 320px;
  }
  .mfa-banner-text strong {
    color: var(--glow);
  }
  .mfa-banner-text span {
    color: var(--text-dim);
  }
  .mfa-banner-actions {
    display: flex;
    gap: 0.5rem;
  }
  .mfa-banner-primary {
    background: var(--accent);
    color: var(--bg);
    border: 1px solid var(--accent);
    border-radius: 4px;
    padding: 0.5rem 0.95rem;
    font-size: 0.85rem;
    font-weight: 600;
    font-family: inherit;
    cursor: pointer;
  }
  .mfa-banner-primary:hover {
    background: var(--glow);
    border-color: var(--glow);
  }
  .mfa-banner-dismiss {
    background: transparent;
    color: var(--text-dim);
    border: 1px solid var(--rule);
    border-radius: 4px;
    padding: 0.5rem 0.85rem;
    font-size: 0.85rem;
    font-family: inherit;
    cursor: pointer;
  }
  .mfa-banner-dismiss:hover {
    color: var(--text);
    border-color: var(--text-dim);
  }
  .site-footer {
    margin-top: 2.5rem;
    padding-top: 1rem;
    border-top: 1px solid rgba(232, 232, 234, 0.12);
    font-size: 0.85rem;
    opacity: 0.7;
  }
  .site-footer p {
    margin: 0;
  }
  .site-footer a {
    color: inherit;
    text-decoration: underline;
  }
  .site-footer a:hover,
  .site-footer a:focus-visible {
    color: #d6e6ff;
  }
</style>
